import { useState } from 'react';
import { useStore } from '@nanostores/react';
import { Button, Tooltip } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { AddPeopleModal } from '@/features/add-people';
import { useAnalytics } from '@/services/analytics';
import { billing } from '@/services/billing';
import { useProductTaxonomy } from '@/services/product-taxonomy';

/** The route name the add-people analytics events report for this page. */
const PAGE_NAME = 'people.list.index';

/**
 * The page-header "Add people" action: opens the add-people catalog in a modal.
 * The same catalog is also reachable as a full page at `/profiles/create`.
 * Disabled without the create permission, with the tooltip explaining why.
 */
export function AddPeopleButton() {
  const taxonomy = useProductTaxonomy();
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  const isBuilder = useStore(billing.isBuilder);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const canCreate = hasPermission('journeys/people/create');

  const openModal = () => {
    setIsModalOpen(true);
    void track('people', 'addPeopleModalViewed', {});
  };

  const handleActionClick = (title: string) => {
    void track('people', 'addPeopleActionClicked', { 'Action Name': title, 'Page Name': PAGE_NAME });
  };

  return (
    <>
      <Tooltip content="Contact an admin to access this feature." isDisabled={canCreate} isCustom>
        {(context) => (
          <Button
            variant="primary"
            isDisabled={!canCreate}
            onClick={openModal}
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            Add {taxonomy.term('people.plural')}
          </Button>
        )}
      </Tooltip>
      <AddPeopleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isBuilder={isBuilder}
        onActionClick={handleActionClick}
      />
    </>
  );
}
